import React, { useState, useEffect, memo } from 'react';
import { getAlgoliaUsage } from '@/lib/api';
import { AlgoliaUsageResponse } from '@/types/firestore';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import { RefreshCw, AlertTriangle, Database } from 'lucide-react';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';

interface UsageDisplayProps {
  title: string;
  count: number;
  limit: number | null;
  percentage: number;
}

const UsageDisplay: React.FC<UsageDisplayProps> = ({ title, count, limit, percentage }) => {
  return (
    <div className="space-y-2">
      <div className="flex justify-between">
        <span className="text-sm font-medium">{title}</span>
        <span className="text-sm text-muted-foreground">
          {count.toLocaleString()} {limit ? `/ ${limit.toLocaleString()}` : ''}
        </span>
      </div>
      <Progress value={percentage} className="h-2" />
      <p className="text-xs text-muted-foreground">{percentage.toFixed(1)}% used</p>
    </div>
  );
};

interface CompactUsageDisplayProps {
  title: string;
  count: number;
  limit?: number | null;
  percentage?: number;
}

const CompactUsageDisplay: React.FC<CompactUsageDisplayProps> = ({ title, count, percentage }) => {
  return (
    <div className="flex items-center justify-between py-1">
      <span className="text-xs">{title}</span>
      <span className="text-xs font-medium">
        {count.toLocaleString()}
        {percentage !== undefined && (
          <span className="text-xs text-muted-foreground ml-1">
            ({percentage.toFixed(1)}%)
          </span>
        )}
      </span>
    </div>
  );
};

interface AlgoliaUsageStatsProps {
  variant?: 'default' | 'compact';
  refreshKey?: number;
}

// Memoize the component to prevent unnecessary re-renders
export const AlgoliaUsageStats = memo(({ 
  variant = 'default',
  refreshKey = 0
}: AlgoliaUsageStatsProps) => {
  const [usageData, setUsageData] = useState<AlgoliaUsageResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchUsageData = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getAlgoliaUsage();
      setUsageData(data);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch Algolia usage data';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Only fetch when component mounts or refreshKey changes
  useEffect(() => {
    fetchUsageData();
    
    // Cleanup function to avoid state updates on unmounted component
    return () => {
      // This empty cleanup function ensures we don't update state after unmount
    };
  }, [refreshKey]);

  const handleRefresh = () => {
    fetchUsageData();
  };

  if (loading) {
    if (variant === 'compact') {
      return (
        <div className="space-y-2">
          <Skeleton className="h-3 w-full" />
          <Skeleton className="h-3 w-3/4" />
        </div>
      );
    }
    
    return (
      <Card>
        <CardHeader>
          <CardTitle>Algolia Usage Statistics</CardTitle>
          <CardDescription>Loading current usage data...</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-20 w-full" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    if (variant === 'compact') {
      return (
        <div className="text-xs text-red-500 flex items-center gap-1">
          <AlertTriangle className="h-3 w-3" />
          Error loading data
        </div>
      );
    }
    
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-yellow-500" />
            Error Loading Usage Data
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">{error}</p>
          <Button onClick={handleRefresh} variant="outline" size="sm" className="mt-4">
            <RefreshCw className="mr-2 h-4 w-4" />
            Try Again
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (!usageData) {
    return null;
  }

  const { records, operations_last_30_days, sync } = usageData.usage;
  const { drinks, locations, total, limit, percentage } = records;

  // Calculate percentage for individual record types
  const drinksPercentage = limit ? (drinks / limit) * 100 : 0;
  const locationsPercentage = limit ? (locations / limit) * 100 : 0;

  if (variant === 'compact') {
    return (
      <div className="text-xs space-y-2">
        <div className="flex items-center justify-between">
          <span className="font-medium">Total Records:</span>
          <Badge variant={percentage > 80 ? "destructive" : "outline"} className="text-xs h-5">
            {total.toLocaleString()} {limit ? `/ ${limit.toLocaleString()}` : ''}
          </Badge>
        </div>
        <Progress value={percentage} className="h-1.5" />
        
        <div className="grid grid-cols-2 gap-x-2 mt-1">
          <CompactUsageDisplay title="Drinks" count={drinks} percentage={drinksPercentage} />
          <CompactUsageDisplay title="Locations" count={locations} percentage={locationsPercentage} />
        </div>
        
        <Separator className="my-1" />
        
        <div className="grid grid-cols-2 gap-2 text-center">
          <div className="border rounded-sm p-1">
            <div className="text-[10px] text-muted-foreground">Searches</div>
            <div className="font-medium">{operations_last_30_days.searches}</div>
          </div>
          <div className="border rounded-sm p-1">
            <div className="text-[10px] text-muted-foreground">Indexing</div>
            <div className="font-medium">{operations_last_30_days.indexing}</div>
          </div>
        </div>
        
        <div className="flex items-center justify-between mt-1">
          <span className="text-[10px] text-muted-foreground">Last Sync:</span>
          <span className="text-[10px]">
            {sync.lastFullSync 
              ? new Date(sync.lastFullSync).toLocaleDateString() 
              : 'Never'}
          </span>
        </div>
      </div>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Database className="h-5 w-5" />
            <CardTitle>Algolia Usage Statistics</CardTitle>
          </div>
          <Button onClick={handleRefresh} variant="ghost" size="icon">
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
        <CardDescription>Current search index usage and operations</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          {/* Total Records */}
          <div>
            <h3 className="text-sm font-medium mb-3">Total Records</h3>
            <UsageDisplay 
              title="All Records" 
              count={total} 
              limit={limit} 
              percentage={percentage} 
            />
          </div>

          {/* Breakdown by Record Type */}
          <div>
            <h3 className="text-sm font-medium mb-3">Records by Type</h3>
            <div className="space-y-4">
              <UsageDisplay 
                title="Drinks" 
                count={drinks} 
                limit={limit} 
                percentage={drinksPercentage} 
              />
              <UsageDisplay 
                title="Locations" 
                count={locations} 
                limit={limit} 
                percentage={locationsPercentage} 
              />
            </div>
          </div>
          
          <Separator />
          
          {/* Operations */}
          <div>
            <h3 className="text-sm font-medium mb-3">Operations (Last 30 Days)</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="text-center p-3 bg-muted rounded-md">
                <p className="text-sm font-medium">Searches</p>
                <p className="text-xl mt-1">{operations_last_30_days.searches.toLocaleString()}</p>
              </div>
              <div className="text-center p-3 bg-muted rounded-md">
                <p className="text-sm font-medium">Indexing</p>
                <p className="text-xl mt-1">{operations_last_30_days.indexing.toLocaleString()}</p>
              </div>
            </div>
            <p className="text-sm text-muted-foreground mt-2">
              Total: {operations_last_30_days.total.toLocaleString()} operations
            </p>
          </div>
          
          <Separator />
          
          {/* Sync Status */}
          <div>
            <h3 className="text-sm font-medium mb-3">Sync Status</h3>
            <div className="flex flex-col gap-2">
              <div className="flex justify-between items-center">
                <span className="text-sm">Mode:</span>
                <Badge variant={sync.mode === 'normal' ? 'default' : 'destructive'}>
                  {sync.mode === 'normal' ? 'Active' : sync.mode === 'importing' ? 'Importing' : 'Disabled'}
                </Badge>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm">Incremental Sync:</span>
                <Badge variant={sync.incrementalEnabled ? 'default' : 'secondary'}>
                  {sync.incrementalEnabled ? 'Enabled' : 'Disabled'}
                </Badge>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm">Last Full Sync:</span>
                <span className="text-sm text-muted-foreground">
                  {sync.lastFullSync 
                    ? new Date(sync.lastFullSync).toLocaleString() 
                    : 'Never'}
                </span>
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
});

// Add displayName for better debugging
AlgoliaUsageStats.displayName = 'AlgoliaUsageStats'; 